import { FetchArgs } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { getFirebaseErrorCode } from "@utils/firebase";
import logger from "@utils/logger";
import { maskSensitiveData } from "@utils/misc";
import { APIErrorCodes } from "kz-ui-sdk";
import { isNil, omitBy } from "lodash-es";
import { TAG_VALUE_MAX_LENGTH, getStatusClass, normalizeEndpoint } from "./api-error-tags";
import { getSentryManual, sentryManual } from "./manual-sentry-lazy";

export const SENSITIVE_FIELDS = [
  "password",
  "pin",
  "client_secret",
  "phone",
  "client_id",
  "accNo",
  "key",
  "firstname",
  "lastname",
  "secret",
  "code",
  "seonSession",
  // PII: player email, full name, and the personal-info `info` bag (raw national
  // IDs — PAN/CPF/CURP/DNI/...). Masked everywhere standardizeSentryRequest runs.
  "email",
  "accountName",
  "info",
];

const IGNORE_ERROR_MESSAGES = [
  APIErrorCodes.Auth.Required,
  APIErrorCodes.Auth.TokenExpired,
  APIErrorCodes.Auth.TokenInvalidated,
  APIErrorCodes.Auth.InvalidCredential,
  APIErrorCodes.Auth.InvalidCredentials,
];

const IGNORE_ENDPOINTS = ["/member/referral/reward/daily-claim", "/member/game/gateway"];

export const standardizeSentryRequest = (requestBody?: any): any => {
  return maskSensitiveData(requestBody, SENSITIVE_FIELDS, "******");
};

/** Groups every failed API call into one Sentry issue. Must match the server-side fingerprint rule. */
const API_CALL_FAILED_FINGERPRINT = "api-call-failed";

/** Fixed exception message; real URL goes into the `endpoint` tag instead. */
const API_CALL_FAILED_MESSAGE = "API call failed";

/**
 * Reports a failed API call to Sentry under one fingerprinted issue.
 *
 * @param args - RTK Query args (string URL or {@link FetchArgs})
 * @param response - RTK Query error response
 */
export const captureApiException = (args: string | FetchArgs, response: any) => {
  // String form has no body to report.
  const url = typeof args === "string" ? args : args?.url;
  const request = typeof args === "string" ? undefined : args?.body;
  const statusCode = response?.status;
  const errorType = response?.data?.error?.type;
  const errorMessage = response?.data?.error?.message;

  // ignore network error
  if (response?.data?.error === "Failed to fetch") {
    return;
  }

  // ignore non response error
  if (!response?.data) {
    return;
  }

  // ignore ignore error messages
  if (IGNORE_ERROR_MESSAGES.includes(errorMessage)) {
    return;
  }

  // ignore ignore endpoints start with
  if (url && IGNORE_ENDPOINTS.some((endpoint) => url.startsWith(endpoint))) {
    return;
  }

  const error = new Error(API_CALL_FAILED_MESSAGE);
  error.name = "ApiCallFailedError";

  Sentry.captureException(error, {
    fingerprint: [API_CALL_FAILED_FINGERPRINT],
    extra: {
      // Unnormalized URL, for finding the exact failing request.
      endpointUrl: url,
      request: standardizeSentryRequest(request),
      response: standardizeSentryRequest(response?.data),
    },
    tags: omitBy(
      {
        endpoint: normalizeEndpoint(url),
        errorType: errorType,
        statusCode: statusCode,
        statusClass: getStatusClass(statusCode),
        // Breaks the single issue down by cause (e.g. "wrong code").
        errorMessage: typeof errorMessage === "string" ? normalizeErrorMessage(errorMessage) : undefined,
      },
      isNil,
    ),
  });
};

/**
 * Strips dynamic parts (URLs, hashes, numbers) from a message to make it usable as a Sentry tag.
 *
 * @param message - Raw error message
 * @returns Normalized message, capped at {@link TAG_VALUE_MAX_LENGTH}
 */
export const normalizeErrorMessage = (message: string): string => {
  return (
    message
      .replace(/https?:\/\/[^\s)'"]+/gi, "<url>")
      // Chunk hashes, build ids: alphanumeric, 8+ chars, at least one digit.
      .replace(/\b(?=[a-z0-9]{8,}\b)[a-z0-9]*\d[a-z0-9]*\b/gi, "<id>")
      // Pure hex runs the rule above misses.
      .replace(/\b[a-f]{8,}\b/gi, "<hash>")
      .replace(/\d+/g, "<n>")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, TAG_VALUE_MAX_LENGTH)
  );
};

/** Reports a non-cancellation Google-auth popup failure (e.g. `auth/popup-blocked`). */
export const captureGoogleAuthPopupError = (error: unknown, mode: string): void => {
  try {
    const errorCode = getFirebaseErrorCode(error);
    const errorMessage =
      error && typeof error === "object" && "message" in error
        ? String((error as { message: unknown }).message)
        : undefined;

    sentryManual.captureMessage(
      "Google auth popup failed",
      "warning",
      { errorMessage },
      { errorCode: errorCode ?? "unknown", mode },
    );
  } catch (captureError) {
    logger._console.error("captureGoogleAuthPopupError failed", captureError);
  }
};

export const capturePageCrashError = async (
  error: unknown,
  extra?: Record<string, unknown>,
): Promise<string | undefined> => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  errorObj.name = "Page Crashes Error";

  const errorMessage = errorObj.message || String(error);
  const errorStack = errorObj.stack || "";
  const errorName = errorObj.name || "Error";

  // Tag with the (normalized) underlying message so the single "Page Crashes
  // Error" issue can be counted/broken down by actual cause in Sentry —
  // extra_data.error_message is not indexed and cannot be aggregated. Passed
  // per-call (not setTag) so it stays on this event and never leaks to others.
  const normalizedMessage = normalizeErrorMessage(errorMessage);

  const manual = await getSentryManual();
  return manual.captureMessage(
    errorName,
    "error",
    {
      error_message: errorMessage,
      error_stack: errorStack,
      original_error: errorObj.toString(),
      ...extra,
    },
    normalizedMessage ? { error_message: normalizedMessage } : undefined,
  );
};
